// src/components/desk/PropertyModalNav.jsx — the add/edit property modal's jump navigation
// (redesign stage 1, 2026-09-16, from the designer's mockup: a sidebar with status dots on desktop,
// a scrolling chip strip on the phone).
//
// Draws only. The modal decides each section's status; this renders it. Its own file so it can be
// mounted outside the login gate, like BuildingFacts and FeatureCapture.
//
// Status vocabulary — the same four words everywhere, never a colour alone (a dot + a word/glyph):
//   done   — answered            (emerald dot, ✓ on the chip)
//   needs  — needs you           (amber dot, ! on the chip): required and missing, a conflict, an error
//   empty  — nothing recorded    (hollow dot)
//   locked — after the first save (lock icon)
import { t } from '../../lib/i18n.js'
import { Icon } from '../Icon.jsx'

const DOT = {
  done: 'bg-emerald-500 border-emerald-500',
  needs: 'bg-amber-500 border-amber-500',
  empty: 'bg-transparent border-muted-foreground/50',
  locked: 'bg-transparent border-muted-foreground/30',
}
const GROUPS = [['record', 'desk_pm_group_record'], ['in', 'desk_pm_group_in'], ['work', 'desk_pm_group_work']]

export function progressOf(sections) {
  const record = (sections || []).filter(s => s.group === 'record')
  return { done: record.filter(s => s.status === 'done').length, total: record.length }
}

export function PropertyModalProgress({ sections }) {
  const { done, total } = progressOf(sections)
  if (!total) return null
  return (
    <span className="hidden sm:flex items-center gap-2 text-xs text-muted-foreground tabular-nums" data-pm-progress>
      <span>{done}/{total}</span>
      <span className="w-16 h-1 rounded-full bg-muted overflow-hidden" aria-hidden>
        <span className="block h-full bg-emerald-500" style={{ width: `${Math.round((done / total) * 100)}%` }} />
      </span>
    </span>
  )
}

export function PropertyModalNav({ sections, onJump, variant = 'side', active = null }) {
  const list = sections || []
  if (variant === 'chips') {
    return (
      <div className="lg:hidden -mx-1 flex gap-1.5 overflow-x-auto pb-1" data-pm-chips>
        {list.map(s => (
          <button key={s.id} type="button" onClick={() => onJump(s.id)}
            className={`shrink-0 inline-flex items-center gap-1 rounded-full border px-3 min-h-9 text-xs whitespace-nowrap ${
              active === s.id ? 'bg-foreground text-background border-foreground' : 'border-border text-muted-foreground'}`}>
            {s.label}
            {s.count && <span className="tabular-nums">{s.count}</span>}
            {!s.count && s.status === 'done' && <span aria-label={t('desk_pm_status_done')}>✓</span>}
            {s.status === 'needs' && <span className="text-amber-600 dark:text-amber-400" aria-label={t('desk_pm_status_needs')}>!</span>}
            {s.status === 'locked' && <Icon name="lock" />}
          </button>
        ))}
      </div>
    )
  }
  return (
    <nav className="hidden lg:block sticky top-20 self-start text-[13px]" data-pm-nav aria-label={t('desk_pm_nav_label')}>
      {GROUPS.map(([g, labelKey]) => {
        const rows = list.filter(s => s.group === g)
        if (!rows.length) return null
        return (
          <div key={g} className="mb-4">
            <p className="text-[11px] font-medium text-muted-foreground mb-1">{t(labelKey)}</p>
            <ul className="space-y-0.5">
              {rows.map(s => (
                <li key={s.id}>
                  <button type="button" onClick={() => onJump(s.id)} data-pm-jump={s.id}
                    className={`w-full flex items-center gap-2 rounded-md px-2 py-1 text-left hover:bg-muted ${
                      active === s.id ? 'bg-muted text-foreground' : 'text-muted-foreground'}`}>
                    {s.status === 'locked'
                      ? <span className="w-2 shrink-0 text-muted-foreground/60"><Icon name="lock" size={10} /></span>
                      : <span className={`w-2 h-2 shrink-0 rounded-full border ${DOT[s.status] || DOT.empty}`} aria-hidden />}
                    <span className="flex-1 min-w-0 truncate">{s.label}</span>
                    {s.count && <span className="text-[11px] tabular-nums text-muted-foreground">{s.count}</span>}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )
      })}
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted-foreground border-t border-border/60 pt-2">
        <span className="inline-flex items-center gap-1"><span className={`w-2 h-2 rounded-full border ${DOT.done}`} />{t('desk_pm_status_done')}</span>
        <span className="inline-flex items-center gap-1"><span className={`w-2 h-2 rounded-full border ${DOT.needs}`} />{t('desk_pm_status_needs')}</span>
        <span className="inline-flex items-center gap-1"><span className={`w-2 h-2 rounded-full border ${DOT.empty}`} />{t('desk_pm_status_empty')}</span>
      </div>
    </nav>
  )
}
